import { Button, makeFocusRing } from '@workramp/ui';
import styled, { css } from 'styled-components';
import chroma from 'chroma-js';

export interface StyledButtonProps {
  $color: string;
  $hoverColor?: string;
  $textColor: string;
  $fontFamily: string;
  $fontSize: number;
  $borderColor?: string;
}

const makeSolid = ({ color, textColor, hoverColor, borderColor, isDisabled }) =>
  !isDisabled &&
  css`
    color: ${textColor};
    background-color: ${color};
    border-color: ${borderColor || color};

    &:link,
    &:visited {
      color: ${textColor};
      background-color: ${color};
      border-color: ${borderColor || color};
    }

    &:link:hover,
    &:visited:hover,
    &:not(:disabled):hover {
      background-color: ${chroma(hoverColor || color)
        .alpha(0.8)
        .hex()};
      border-color: ${chroma(borderColor || hoverColor || color)
        .alpha(0.8)
        .hex()};
    }

    &:focus {
      outline: none;
      ${makeFocusRing(color)};
    }
  `;

const makeOutline = ({ color, hoverColor, borderColor, isDisabled }) =>
  !isDisabled &&
  css`
    color: ${color};
    background-color: transparent;
    border-color: ${borderColor || chroma(color).alpha(0.1).hex()};

    &:link,
    &:visited {
      color: ${color};
      background-color: transparent;
      border-color: ${borderColor || chroma(color).alpha(0.1).hex()};
    }

    &:link:hover,
    &:visited:hover,
    &:not(:disabled):hover {
      color: ${chroma(hoverColor || color)
        .darken(0.1)
        .hex()};
      background-color: ${chroma(hoverColor || color)
        .alpha(0.05)
        .hex()};
    }

    &:focus {
      outline: none;
      ${makeFocusRing(color)};
    }
  `;

const makeText = ({ color, hoverColor, isDisabled }) =>
  !isDisabled &&
  css`
    color: ${color};
    background-color: transparent;
    border-color: transparent;

    &:link,
    &:visited {
      color: ${color};
      background-color: transparent;
      border-color: transparent;
    }

    &:link:hover,
    &:visited:hover,
    &:not(:disabled):hover {
      color: ${chroma(hoverColor || color)
        .darken(0.1)
        .hex()};
    }

    &:focus {
      outline: none;
      ${makeFocusRing(color)};
    }
  `;

const makeInlineText = ({ color, hoverColor, isDisabled }) =>
  !isDisabled &&
  css`
    color: ${color};
    background-color: transparent;
    border-color: transparent;

    &:link,
    &:visited {
      color: ${color};
      background-color: transparent;
      border-color: transparent;
    }

    &:link:hover,
    &:visited:hover,
    &:not(:disabled):hover {
      color: ${chroma(hoverColor || color)
        .darken(0.1)
        .hex()};
    }

    &:focus {
      outline: none;
      ${makeFocusRing(color)};
    }
  `;

const variantMap = {
  solid: makeSolid,
  outline: makeOutline,
  text: makeText,
  'text-inline': makeInlineText,
};

export const StyledButton = styled(Button)<StyledButtonProps>`
  ${({ variant = 'solid', $color, $textColor, $hoverColor, $borderColor, isDisabled = false }) =>
    variantMap[variant]({
      color: $color,
      textColor: $textColor,
      hoverColor: $hoverColor,
      borderColor: $borderColor,
      isDisabled,
    })};

  span:not(:has(svg)) {
    font-family: ${({ $fontFamily }) => $fontFamily};
    font-size: ${({ $fontSize }) => $fontSize}px;
    line-height: normal;
  }
`;
